import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { makeStyles, withStyles } from "@mui/styles";
import {
  Switch,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  VerifiedUser,
  Warning,
  Image,
  Assignment,
  Badge,
  CheckCircle,
  Cancel,
  Edit,
  CloudUpload,
  Save,
} from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../components/config/config";
import { useSelector } from "react-redux";

const CustomSwitch = withStyles({
  switchBase: {
    color: "#303F9F",
    "&$checked": {
      color: "#303F9F",
    },
    "&$checked + $track": {
      backgroundColor: "#303F9F",
    },
  },
  checked: {},
  track: {},
})(Switch);

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "600px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 0,
  overflow: "hidden",
};

const useStyles = makeStyles({
  cardHover: {
    transition: "transform 0.3s ease-in-out, shadow 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    },
  },
  verifiedBadge: {
    background: "linear-gradient(45deg, #4CAF50, #66BB6A)",
    color: "white",
    fontWeight: "bold",
  },
  notVerifiedBadge: {
    background: "linear-gradient(45deg, #F44336, #EF5350)",
    color: "white",
    fontWeight: "bold",
  },
  editButton: {
    backgroundColor: "rgba(255,255,255,0.9)",
    "&:hover": {
      backgroundColor: "white",
    },
  },
  fileInput: {
    display: "none",
  },
  pendingChange: {
    border: "2px solid #FFA000",
  },
  documentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  documentTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

export default function ViewVerify({
  isVerificationPopupOpen,
  toggleVerificationDetailsPopup,
  selectedCenterData,
  technicianDetails,
  handleSwitchToggle,
  handleAdharFront,
  aadharBack,
  panSwitch,
  aadharFront,
  onDocumentsUpdate,
}) {
  const center_Data = selectedCenterData?.document_details;
  const centerId = selectedCenterData?._id;
  const technicianId = technicianDetails?._id;
  const classes = useStyles();
  const globalState = useSelector((state) => state.AdminReducer);
  const accessToken = globalState?.userTokens?.accessToken;
  const [state, setState] = useState({
    raised: false,
    shadow: 1,
  });
  const [aadharNumber, setAadharNumber] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // State to track pending document changes
  const [pendingChanges, setPendingChanges] = useState({
    pan: null,
    aadharFront: null,
    aadharBack: null,
    profile: null,
    certificate: null,
  });

  const [editMode, setEditMode] = useState({
    pan: false,
    aadharFront: false,
    aadharBack: false,
    profile: false,
    certificate: false,
  });

  const switchToggleHandler = () => {
    handleSwitchToggle();
  };

  const switchAdharFrontHandler = () => {
    handleAdharFront();
  };

  const handleChange = (event) => {
    setAadharNumber(event.target.value);
    setError("");
    setSuccess(false);
  };

  const handleSubmit = async () => {
    if (aadharNumber.length !== 12) {
      setError("Aadhar number must be exactly 12 digits");
      return;
    }

    if (!/^\d+$/.test(aadharNumber)) {
      setError("Aadhar number must contain only digits");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}admin/center-documents-verify`,
        {
          aadhar_number: aadharNumber,
          center_obj_id: centerId,
        }
      );
      console.log(response.data);
      setSuccess(true);
      setError("");
    } catch (error) {
      console.error("Error:", error);
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle file selection and local storage
  const handleFileChange = (event, documentType) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "application/pdf",
      ];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, WebP) or PDF");
        return;
      }

      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }

      // Create object URL for preview
      const objectUrl = URL.createObjectURL(file);

      // Save file locally in state
      setPendingChanges((prev) => ({
        ...prev,
        [documentType]: {
          file: file,
          previewUrl: objectUrl,
          fileName: file.name,
          fileType: file.type,
        },
      }));

      // Exit edit mode
      setEditMode((prev) => ({ ...prev, [documentType]: false }));

      setError("");
    }
  };

  // Function to cancel pending change
  const cancelPendingChange = (documentType) => {
    if (pendingChanges[documentType]?.previewUrl) {
      URL.revokeObjectURL(pendingChanges[documentType].previewUrl);
    }

    setPendingChanges((prev) => ({
      ...prev,
      [documentType]: null,
    }));
  };

  const toggleEditMode = (documentType) => {
    setEditMode((prev) => ({ ...prev, [documentType]: !prev[documentType] }));
    setError("");
    setSuccess(false);
  };

  // Function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to save all changes to API
  const handleSaveVerification = async () => {
    setSaveLoading(true);
    setError("");

    try {
      const updatePayload = {
        center_obj_id: centerId,
        technician_obj_id: technicianId,
        document_details: { ...center_Data },
        personal_details: { ...selectedCenterData.personal_details },
      };

      console.log("Update Payload before files:", updatePayload);

      let hasChanges = false;

      for (const [documentType, fileData] of Object.entries(pendingChanges)) {
        if (fileData) {
          hasChanges = true;
          const base64String = await fileToBase64(fileData.file);

          switch (documentType) {
            case "pan":
              updatePayload.document_details.pan_card_document = base64String;
              updatePayload.document_details.pan_verified = false;
              break;
            case "aadharFront":
              updatePayload.document_details.aadhar_card_document = {
                ...updatePayload.document_details.aadhar_card_document,
                front_side: base64String,
                aadhar_front_verified: false,
              };
              break;
            case "aadharBack":
              updatePayload.document_details.aadhar_card_document = {
                ...updatePayload.document_details.aadhar_card_document,
                back_side: base64String,
                aadhar_back_verified: false,
              };
              break;
            case "profile":
              updatePayload.personal_details.profile_picture = base64String;
              break;
            case "certificate":
              updatePayload.personal_details.company_worked_with = base64String;
              break;
            default:
              break;
          }
        }
      }

      if (!hasChanges) {
        setError("No changes to save");
        setSaveLoading(false);
        return;
      }

      const updateResponse = await axios.put(
        `${BASE_URL}admin/update-center-by-admin`,
        updatePayload,
        {
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Documents updated successfully:", updateResponse.data);

      // Clean up object URLs
      Object.keys(pendingChanges).forEach((key) => {
        if (pendingChanges[key]?.previewUrl) {
          URL.revokeObjectURL(pendingChanges[key].previewUrl);
        }
      });

      setPendingChanges({
        pan: null,
        aadharFront: null,
        aadharBack: null,
        profile: null,
        certificate: null,
      });

      setSuccess(true);
      setError("");

      // Pass centerId to the callback to ensure API call works properly
      if (onDocumentsUpdate && centerId) {
        onDocumentsUpdate(centerId);
      }

      setTimeout(() => {
        toggleVerificationDetailsPopup();
      }, 1500);
    } catch (error) {
      console.error("Error saving documents:", error);
      setError("Failed to save documents. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const DocumentSection = ({
    title,
    image,
    verified,
    type = "default",
    documentType,
  }) => {
    const currentImage = pendingChanges[documentType]?.previewUrl || image;
    const hasPendingChange = pendingChanges[documentType] !== null;

    return (
      <div className="mb-6">
        <div className={classes.documentHeader}>
          <div className={classes.documentTitle}>
            {type === "pan" && <Assignment className="text-blue-600" />}
            {type === "aadhar" && <Badge className="text-green-600" />}
            {type === "profile" && <Image className="text-purple-600" />}
            {type === "certificate" && (
              <VerifiedUser className="text-orange-600" />
            )}
            <Typography variant="h6" className="font-semibold text-gray-800">
              {title}
            </Typography>
            {hasPendingChange && (
              <Chip
                label="Pending Save"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </div>
          <div className="flex items-center space-x-2">
            {verified !== undefined && (
              <Chip
                icon={verified ? <CheckCircle /> : <Cancel />}
                label={verified ? "Verified" : "Not Verified"}
                className={
                  verified ? classes.verifiedBadge : classes.notVerifiedBadge
                }
                size="small"
              />
            )}

            {editMode[documentType] ? (
              <div className="flex space-x-1">
                <Button
                  size="small"
                  onClick={() => toggleEditMode(documentType)}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex space-x-1">
                {!hasPendingChange && (
                  <IconButton
                    size="small"
                    onClick={() => toggleEditMode(documentType)}
                    className={classes.editButton}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {hasPendingChange && (
                  <IconButton
                    size="small"
                    onClick={() => cancelPendingChange(documentType)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                )}
              </div>
            )}
          </div>
        </div>

        <Card
          className={`${
            classes.cardHover
          } overflow-hidden border border-gray-200 ${
            hasPendingChange ? classes.pendingChange : ""
          }`}
        >
          {editMode[documentType] ? (
            <div className="p-4 bg-gray-50">
              <Typography variant="body2" className="mb-3 text-gray-600">
                Upload new {title.toLowerCase()}
              </Typography>
              <input
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                className={classes.fileInput}
                id={`file-upload-${documentType}`}
                type="file"
                onChange={(e) => handleFileChange(e, documentType)}
              />
              <label htmlFor={`file-upload-${documentType}`}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  className="w-full"
                >
                  Choose File
                </Button>
              </label>
            </div>
          ) : (
            <CardMedia
              component="img"
              height="200"
              image={currentImage}
              alt="Document image"
              sx={{
                objectFit: "contain",
                backgroundColor: "#f8fafc",
              }}
              className="p-2"
            />
          )}
        </Card>

        {hasPendingChange && (
          <Typography variant="caption" className="text-orange-600 mt-1 block">
            {pendingChanges[documentType]?.fileName} - Ready to save
          </Typography>
        )}
      </div>
    );
  };

  const hasPendingChanges = Object.values(pendingChanges).some(
    (change) => change !== null
  );

  return (
    <Modal
      open={isVerificationPopupOpen}
      onClose={toggleVerificationDetailsPopup}
      aria-labelledby="verification-modal"
      aria-describedby="verification-details"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <div className="bg-teal-600 p-6 text-white">
          <div className="flex items-center space-x-3">
            <VerifiedUser className="text-2xl" />
            <div>
              <Typography variant="h5" className="font-bold">
                Document Verification
              </Typography>
              <Typography variant="body2" className="opacity-90">
                View and update center documents
              </Typography>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Status Alert */}
          <Alert
            severity={
              center_Data?.aadhar_card_document?.aadhar_back_verified
                ? "success"
                : "warning"
            }
            icon={
              center_Data?.aadhar_card_document?.aadhar_back_verified ? (
                <CheckCircle />
              ) : (
                <Warning />
              )
            }
            className="mb-6 rounded-lg"
          >
            {center_Data?.aadhar_card_document?.aadhar_back_verified
              ? "All documents have been verified successfully"
              : "Documents verification pending"}
          </Alert>

          {hasPendingChanges && (
            <Alert severity="info" className="mb-4">
              You have unsaved document changes. Click "Save Verification" to
              update all documents.
            </Alert>
          )}

          {error && (
            <Alert severity="error" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" className="mb-4">
              Documents updated successfully!
            </Alert>
          )}

          {/* Pan Card */}
          <DocumentSection
            title="PAN Card"
            image={center_Data?.pan_card_document}
            verified={center_Data?.pan_verified}
            type="pan"
            documentType="pan"
          />

          {/* Aadhar Front */}
          <DocumentSection
            title="Aadhar Card - Front Side"
            image={center_Data?.aadhar_card_document?.front_side}
            verified={center_Data?.aadhar_card_document?.aadhar_front_verified}
            type="aadhar"
            documentType="aadharFront"
          />

          {/* Aadhar Verification Input */}
          {!center_Data?.aadhar_card_document?.aadhar_back_verified && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
              <Typography
                variant="subtitle1"
                className="font-medium text-gray-700"
              >
                Verify Aadhar Number
              </Typography>
              <div className="flex flex-col sm:flex-row gap-4 items-end mt-2">
                <TextField
                  fullWidth
                  placeholder="Enter 12-digit Aadhar Number"
                  label="Aadhar Number"
                  value={aadharNumber}
                  onChange={handleChange}
                  error={Boolean(error)}
                  helperText={error}
                  variant="outlined"
                  size="small"
                  className="flex-1"
                  inputProps={{ maxLength: 12 }}
                />
                <Button
                  onClick={handleSubmit}
                  disabled={loading || aadharNumber.length !== 12}
                  variant="contained"
                  className="bg-teal-600 hover:bg-teal-700 px-6 py-2 rounded-lg font-medium min-w-[120px]"
                >
                  {loading ? <CircularProgress size={24} /> : "Verify"}
                </Button>
              </div>
              {success && (
                <Alert severity="success" className="mt-3">
                  Aadhar number verified successfully!
                </Alert>
              )}
            </div>
          )}

          {/* Aadhar Back */}
          <DocumentSection
            title="Aadhar Card - Back Side"
            image={center_Data?.aadhar_card_document?.back_side}
            verified={center_Data?.aadhar_card_document?.aadhar_back_verified}
            type="aadhar"
            documentType="aadharBack"
          />

          {/* Profile Picture */}
          <DocumentSection
            title="Profile Picture"
            image={selectedCenterData?.personal_details?.profile_picture}
            type="profile"
            documentType="profile"
          />

          {/* Certificate */}
          <DocumentSection
            title="Certificate"
            image={selectedCenterData?.personal_details?.company_worked_with}
            type="certificate"
            documentType="certificate"
          />
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              {hasPendingChanges && (
                <Typography
                  variant="body2"
                  className="text-orange-600 font-medium"
                >
                  {Object.values(pendingChanges).filter(Boolean).length}{" "}
                  document(s) ready to save
                </Typography>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={toggleVerificationDetailsPopup}
                variant="outlined"
                className="border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={saveLoading}
              >
                Close
              </Button>
              <Button
                onClick={handleSaveVerification}
                variant="contained"
                disabled={!hasPendingChanges || saveLoading}
                startIcon={
                  saveLoading ? <CircularProgress size={16} /> : <Save />
                }
                className="bg-green-600 hover:bg-green-700 rounded-lg font-medium"
              >
                {saveLoading ? "Saving..." : "Save Verification"}
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
